// src/components/loginpage/forgotbox/Forgotbox.jsx
import React, { useState } from 'react';
import './Forgotbox.css';
import { API_ENDPOINTS } from '../../../config/apiConfig';

// The backend never reveals whether an account exists, so this is the ONLY
// copy we may show after a successful request — see §1/§7 of the contract.
const NEUTRAL_MESSAGE =
  'If an account matches, a reset link has been sent to its email address. Please check your inbox.';

// POST /api/forgot-password answers 200 for everything except an empty
// identifier, so there is very little to map here.
const mapForgotError = (data, resp) => {
  const reason = data && (data.reason || data.error || data.message);

  if (reason === 'missing_identifier') {
    return 'Please enter your email address or username.';
  }
  // Agreed shape for the coming limit: 429, reason "rate_limited", and
  // retry_after_seconds (backend reply 17 Sep §2 Q1).
  if ((resp && resp.status === 429) || reason === 'rate_limited') {
    const s = Number(data && data.retry_after_seconds);
    if (!s || s <= 0) return 'Too many requests. Try again in a few minutes.';
    return s < 90
      ? 'Too many requests. Try again in about a minute.'
      : `Too many requests. Try again in about ${Math.ceil(s / 60)} minutes.`;
  }
  if (typeof reason === 'string') return `Request failed: ${reason}`;
  if (resp && !resp.ok) return `Request failed with status ${resp.status}`;
  return 'Request failed. Unexpected server response.';
};

const Forgotbox = ({ onNotify, onBack }) => {
  const [identifier, setIdentifier] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const value = identifier.trim();
    if (!value) {
      onNotify && onNotify('Please enter your email address or username.', 'warn');
      return;
    }

    try {
      setIsSubmitting(true);

      const resp = await fetch(API_ENDPOINTS.FORGOT_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: value }),
      });

      let data = null;
      try {
        data = await resp.json();
      } catch {
        // response might not be JSON; ignore parse error
      }

      if (!resp.ok || !data || data.status !== 'success') {
        onNotify && onNotify(mapForgotError(data, resp), 'error');
        return;
      }

      // Deliberately generic — do NOT branch on whether a user was found.
      setSent(true);
      onNotify && onNotify(NEUTRAL_MESSAGE, 'info');
    } catch (err) {
      console.error('Forgot password error:', err);
      onNotify && onNotify('Network or server error. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmit();
    }
  };

  return (
    <div className="forgotbox">
      {sent ? (
        <>
          <div className="forgot-row forgot-item">
            <span className="forgot-title">Check your inbox</span>
          </div>
          <div className="forgot-row forgot-note forgot-item">
            <p>{NEUTRAL_MESSAGE}</p>
          </div>
          <div className="forgot-row forgot-note forgot-item">
            <p>The link is valid for 24 hours and can be used once.</p>
          </div>
          <div className="forgot-row forgot-item">
            <button
              className="forgot-button"
              type="button"
              onClick={() => setSent(false)}
            >
              Send again
            </button>
          </div>
          <div className="forgot-row forgot-item">
            <span className="forgot-text" onClick={onBack}>Back to login</span>
          </div>
        </>
      ) : (
        <>
          <div className="forgot-row forgot-item">
            <span className="forgot-title">Reset your password</span>
          </div>
          <div className="forgot-row forgot-note forgot-item">
            <p>Enter your email address or username and we'll send a reset link to the email on the account.</p>
          </div>

          <div className="forgot-row forgot-item">
            <label htmlFor="forgot-identifier">Email or Username</label>
          </div>
          <div className="forgot-row forgot-item">
            <div className="forgot-input-wrapper">
              <input
                id="forgot-identifier"
                  type="text"
                placeholder="you@example.com"
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="forgot-row forgot-item">
            <button
              className="forgot-button"
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send reset link'}
            </button>
          </div>

          <div className="forgot-row forgot-item">
            <span className="forgot-text" onClick={onBack}>Back to login</span>
          </div>
        </>
      )}
    </div>
  );
};

export default Forgotbox;
